import { z } from 'zod'
import { Timestamp } from './common.js'
import { RoutePublic } from './route.js'
import { DisplayName } from './user.js'
import { SystemNotification } from './notification.js'

export const WatchTargetType = z.enum(['route'])
export type WatchTargetType = z.infer<typeof WatchTargetType>

export const Watch = z.object({
  user_id: z.string().uuid(),
  target_type: WatchTargetType,
  target_id: z.string().uuid(),
  since: Timestamp,
  last_seen_etag: z.string().nullable(),
})
export type Watch = z.infer<typeof Watch>

export const WatchCreateInput = z.object({
  target_type: WatchTargetType,
  target_id: z.string().uuid(),
})
export type WatchCreateInput = z.infer<typeof WatchCreateInput>

export const PendingReviewItem = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('proposal'),
    route_id: z.string().uuid(),
    proposal_id: z.string().uuid(),
    proposed_at: Timestamp,
    author_id: z.string().uuid(),
    author_name: DisplayName.nullable().optional(),
  }),
  z.object({
    kind: z.literal('publish'),
    route: RoutePublic.pick({ id: true, slug: true, title: true }),
    requested_at: Timestamp,
    author_id: z.string().uuid(),
    author_name: DisplayName.nullable().optional(),
  }),
])
export type PendingReviewItem = z.infer<typeof PendingReviewItem>

export const InboxResponse = z.object({
  watched_changes: z.array(
    z.object({
      route: RoutePublic.pick({ id: true, slug: true, title: true }),
      old_etag: z.string().nullable(),
      new_etag: z.string(),
      changed_at: Timestamp,
    }),
  ),
  pending_reviews: z.array(PendingReviewItem),
  saved_search_hits: z.array(
    z.object({
      search_id: z.string().uuid(),
      search_name: z.string(),
      new_route_id: z.string().uuid(),
      matched_at: Timestamp,
    }),
  ),
  system_notifications: z.array(SystemNotification),
  unread_system_notification_count: z.number().int().nonnegative(),
  next_system_notification_cursor: z.string().nullable(),
})
export type InboxResponse = z.infer<typeof InboxResponse>

export const InboxUnreadCountResponse = z.object({
  unread_system_notification_count: z.number().int().nonnegative(),
})
export type InboxUnreadCountResponse = z.infer<typeof InboxUnreadCountResponse>

export const InboxSeenRequest = z.object({
  watched_changes: z
    .array(
      z.object({
        route_id: z.string().uuid(),
        new_etag: z.string(),
      }),
    )
    .default([]),
  saved_search_hits: z
    .array(
      z.object({
        search_id: z.string().uuid(),
        matched_at: Timestamp,
      }),
    )
    .default([]),
  system_notification_ids: z.array(z.string().uuid()).max(50).default([]),
})
export type InboxSeenRequest = z.infer<typeof InboxSeenRequest>
